import { Injectable } from '@angular/core';
import { DynamicsClientService } from '../dynamics-client/dynamics-client.service';
import { MedProFilter, MedProFilterFieldKey, MedProHcp, SyncResponse } from './medpro.models';
import { getFilterFieldDef } from './medpro-filter-fields';

const SEARCH_COLUMNS: (keyof MedProHcp)[] = [
  'indskr_medprohcpid', 'indskr_medproid', 'indskr_medpromasterid',
  'indskr_name', 'indskr_firstname', 'indskr_lastname', 'indskr_middlename',
  'indskr_primaryspecialty', 'indskr_professionaldesignation',
  'indskr_addressline1', 'indskr_city', 'indskr_addressstate', 'indskr_zip', 'indskr_country',
  'indskr_npinumber', 'indskr_deanumber', 'indskr_status',
  'indskr_licensenumber', 'indskr_licensestate',
  'indskr_overallsampleability',
];

// Fields where `startswith` is semantically correct. The Data Provider's
// QueryTranslator collapses startswith → "value*" for the MedPro search API.
const STARTSWITH_FIELDS: MedProFilterFieldKey[] = [
  // 'firstName', 'lastName', 'middleName', 'addressLine1', 'city', 'zip',
];

@Injectable({ providedIn: 'root' })
export class MedProDataService {
  constructor(private dynamicsClient: DynamicsClientService) {}

  /** Pure OData filter builder. Kept public for spec verification in code review. */
  static buildOdataFilter(filters: MedProFilter[]): string {
    const parts: string[] = [];
    for (const f of filters) {
      if (!f.value || !f.value.trim()) continue;
      const def = getFilterFieldDef(f.field);
      const escaped = f.value.replace(/'/g, "''");
      if (STARTSWITH_FIELDS.includes(f.field)) {
        parts.push(`startswith(${def.attribute}, '${escaped}')`);
      } else {
        parts.push(`${def.attribute} eq '${escaped}'`);
      }
    }
    return parts.join(' and ');
  }

  async searchHcps(filters: MedProFilter[], top = 50): Promise<MedProHcp[]> {
    const filter = MedProDataService.buildOdataFilter(filters);
    if (!filter) return [];
    const response = await this.dynamicsClient.retrieveMultipleRequest<MedProHcp>({
      collection: 'indskr_medprohcps',
      select: SEARCH_COLUMNS as string[],
      filter,
      top,
    });
    return response?.value ?? [];
  }

  async addHcpFromMedpro(medProHcpId: string): Promise<SyncResponse> {
    return this.dynamicsClient.executeUnboundAction<SyncResponse>(
      'indskr_MedProSync',
      { Target: 'indskr_medprohcp', RecordId: medProHcpId },
    );
  }

  /** Resolve MedPro master IDs to Dynamics contactIds. Server stores
   *  `indskr_medpromasterid` as a string on the contact (see
   *  Exeevo.MedProSync ContactSyncService.cs). */
  async findContactIdsByMasterIds(masterIds: number[]): Promise<string[]> {
    if (!masterIds || masterIds.length === 0) return [];
    const filter = masterIds
      .map(id => `indskr_medpromasterid eq '${id}'`)
      .join(' or ');
    const response = await this.dynamicsClient.retrieveMultipleRequest<{ contactid: string }>({
      collection: 'contacts',
      select: ['contactid'],
      filter,
    });
    return (response?.value ?? []).map(c => c.contactid).filter(Boolean);
  }

  /**
   * Refresh an existing contact from MedPro.
   * When `medProHcpId` is provided (fallback flow), the server uses that HCP
   * explicitly instead of resolving by MasterID/NPI/DEA. Requires the server-side
   * `MedProHcpId` input on indskr_MedProSync (follow-up noted in spec).
   */
  async refreshContactFromMedpro(
    contactId: string,
    medProHcpId?: string,
  ): Promise<SyncResponse> {
    const payload: Record<string, string> = {
      Target: 'contact',
      RecordId: contactId,
    };
    if (medProHcpId) payload.MedProHcpId = medProHcpId;
    return this.dynamicsClient.executeUnboundAction<SyncResponse>('indskr_MedProSync', payload);
  }
}
